// The reply after Process (2026-10-29, docs/contact-view.md "The reply after
// Process", docs/decisions/2026-10-29-reply-house-template-after-process.md).
//
// A processed refund, return or replacement step returns its house template,
// filled with the step's results: result.replyText, with result.replyTemplate
// ({ folder, title }, server/src/ai/house-template-reply.js). On both layouts
// (the pipeline's and the agent draft panel) the reply box switches to it:
//
//   - the reply as the draft left it: it switches, nothing asks;
//   - the reply edited since the draft: "Replace your reply?" asks
//     (confirmPrompts.js's houseTemplateReplyPrompt()), Keep my reply / Use
//     the <template> reply;
//   - Process all: one switch, after the last step, from the last step in
//     the pass that returned a reply (a note or a check returns none);
//   - a failed or waiting step: no switch;
//   - a step started from Other Actions: no switch (its reply is offered on
//     the step, ManualStepReply.jsx);
//   - a reply that keeps the RMA number chip (RMARTN Cust Pays, already
//     shipped back): no switch, the chip is filled instead (rmaReference.js);
//     unless the step ran as a scrap refund or a company-paid return
//     (2026-10-30: the person switched the step), when the reply follows the
//     step.
//
// The template tag (the reply's inserted template, sent with the decision)
// becomes the new template. Pure: no React, so node --test covers it.

import { hasRmaReferenceToken, withLabelToFollow, hasLabelToFollow, labelToFollowSentence } from '@cxhub/shared'
import { insertHtmlTemplate, insertPlainTemplate } from './templateInsert.js'

// The house reply a step's result carries: { text, template } or null.
export function houseReplyFromResult(action, result) {
  if (!result || typeof result.replyText !== 'string' || !result.replyText.trim()) return null
  if (action?.source === 'manual_panel') return null
  // A label held for the RMA number (2026-10-29, return-label-pending.js) is
  // a waiting step: it never switches the reply (heldLabelReply() instead).
  if (result.labelPending) return null
  const t = result.replyTemplate
  const template = t && typeof t.title === 'string' && t.title ? { folder: t.folder ?? null, title: t.title } : null
  // What the step did (2026-10-30): a scrap or no-return refund, or a
  // company-paid return, is not the customer-paid RMA a "RMARTN Cust Pays"
  // reply describes, so that reply's RMA number chip does not keep it.
  const route = String(result.route ?? '')
  const noReturn = /scrap|refund_no_return|gen_instructions/.test(route)
  return { text: result.replyText, template, ...(noReturn ? { noReturn: true } : {}), ...(result.returnShippingPaidBy === 'company' ? { companyPays: true } : {}) }
}

// The house reply of a Process all pass: the last step in the pass (by the
// order it ran) that succeeded with a tagged house reply (result.replyTemplate:
// a refund, return or replacement outcome). An untagged reply (a resent
// label's "Label Where is it", whose link fills the reply's slot instead)
// never switches the reply here, as before. results: the server's
// [{ index, success, result }]. indexes: the steps in the pass, in order.
// { index, text, template } or null.
export function houseReplyFromRun(actions = [], results = [], indexes = null) {
  const order = Array.isArray(indexes) && indexes.length ? indexes.map(Number) : (results ?? []).map((r) => Number(r?.index))
  for (let k = order.length - 1; k >= 0; k -= 1) {
    const i = order[k]
    const r = (results ?? []).find((x) => Number(x?.index) === i)
    if (!r?.success) continue
    const reply = houseReplyFromResult(actions?.[i], r.result)
    if (reply?.template) return { index: i, ...reply }
  }
  return null
}

// What happens to the reply box: 'switch' (silently), 'ask' (the CSR edited
// it) or 'keep' (no switch). reply: the box as it is now.
export function houseReplyAction({ house, action = null, reply = '', dirty = false } = {}) {
  if (!house) return 'keep'
  // The RMA number chip (2026-10-27): the reply already is the house
  // template; the chip takes the RMA number instead.
  // Not when the step ran as a scrap refund or a company-paid return
  // (2026-10-30, the person switched the step): the reply follows the step.
  if (action?.type === 'process_return_refund' && hasRmaReferenceToken(reply) && !house.noReturn && !house.companyPays) return 'keep'
  return dirty ? 'ask' : 'switch'
}

// The line under the button after Process all switched the reply and
// stopped before sending.
export function houseReplySwitchedNote(template) {
  const name = template?.title ? `"${template.title}"` : 'house'
  return `The steps ran. The reply is now the ${name} template, filled with the results. Check it, then Send.`
}

// The placeholders this Process all pass fills (2026-10-29, Jenn): a Website
// scrap refund drafted up front as "Refund No Return" keeps its amount,
// payment type, dates and RTN number as placeholders, listed on its return
// step (params.processFillsTokens). While that step is in the pass (checked,
// not yet run, not switched to a return), Process all does not wait on
// them. Send never relaxes: a placeholder left after Process still refuses
// the send (findUnresolvedPlaceholders()).
export function tokensFilledByPass(actions = [], indexes = [], actionInputs = {}) {
  const out = new Set()
  for (const i of indexes ?? []) {
    const a = actions?.[i]
    if (a?.type !== 'process_return_refund' || !Array.isArray(a.params?.processFillsTokens)) continue
    if (actionInputs?.[i]?.dispositionOverride === true) continue
    for (const t of a.params.processFillsTokens) if (/^INSERT[A-Z0-9]+$/.test(t)) out.add(t)
  }
  return [...out]
}

// The reply as Process all's placeholder gate reads it: the tokens this pass
// fills taken out. Only for that gate, never for Send.
export function replyForProcessAllGate(reply, tokens = []) {
  let text = String(reply ?? '')
  for (const t of tokens) text = text.replace(new RegExp(`${t}(?![A-Z0-9])`, 'g'), '')
  return text
}

// A return step whose label waits on the RMA number (2026-10-29,
// docs/decisions/2026-10-29-return-label-pending-rma.md): the step
// succeeded but is waiting, so the reply is not switched to the label
// template. The reply the person has keeps their words and says the label
// will follow: in place of a label link slot (withLabelToFollow()), else as
// its own paragraph ABOVE the signature (Jenn, 2026-10-29), found the way a
// template insert finds it (templateInsert.js: the live signature, the eBay
// one-line sign-off); no signature found, at the end. Unchanged when no such
// step ran. results: the [{ success, result }] that ran (one for a single
// Process). signatures/account: as the Templates button passes them.
export function heldLabelReply(reply, results = [], channelKind = 'email', { signatures = [], account = null } = {}) {
  const held = (results ?? []).some((r) => r?.success && r.result?.labelPending)
  const text = String(reply ?? '')
  if (!held || !text.trim() || hasLabelToFollow(text)) return reply
  if (text.includes('INSERTLABELLINK')) return withLabelToFollow(text, channelKind)
  const sentence = labelToFollowSentence(channelKind)
  if (/<[a-z][^>]*>/i.test(text)) return insertHtmlTemplate(text, `<p>${sentence}</p>`, signatures)
  return insertPlainTemplate(text, sentence, { account, signatures }).text
}
